import { Cesium } from 'app/cesium';
import { generate } from 'app/map/geometry/cesium/Generate2DArray';

// Top is rendered in a lower offset.
export enum ZINDEX {
    Zone,
    Asset,
    Indicator, // Indicators should always be above assets
    Transmitter,
    Point,
    EditingShape,
}

export type SortableOffset = {
    id: string | number; // Required
    primitive: HasEyeOffset; // Required
    groupIndex: number; // Required
    offsetInGroup?: number; // used to sort heights.
};

export interface HasEyeOffset {
    eyeOffset: Cesium.Cartesian3;
    groupIndex?: number;
    offsetInGroup?: number;
}

export class OffsetManager {
    private _maxRange: number;
    private _sortableOffsets: Map<string | number, SortableOffset>;
    // Cached 2D Array
    private _sortedGroupedPrimitives: HasEyeOffset[][];

    // two little stateful booleans used to avoid trashing the array generation
    // via the pause() and unpause() methods
    private _isPaused: boolean;
    private _needsGenerating: boolean;

    /**
     * Given a camera distance, group-z-index and sub-z-index
     */
    constructor() {
        this._maxRange = 0;
        this._sortableOffsets = new Map();
        this._sortedGroupedPrimitives = [];
    }

    static get ZINDEX() {
        return ZINDEX;
    }

    /**
     * Stop this instance recalculating offsets, call {@link OffsetManager.unpause} to reverse this action.
     * Useful when bulk adding lost of items.
     */
    pause() {
        this._isPaused = true;
    }

    /**
     * Start recalculating offsets again, use after a {@link OffsetManager.pause} call.
     */
    unpause() {
        this._isPaused = false;
        if (this._needsGenerating) {
            this._needsGenerating = false;
            this._generate2DArray();
        }
    }

    setMaxRange(maxRange: number) {
        // Set new range
        this._maxRange = maxRange;
        // Update offsets given new range.
        this.updateAll();
    }

    set(id: string | number, primitive: HasEyeOffset, groupIndex: number, offsetInGroup = 0) {
        const o = {
            id,
            primitive,
            groupIndex,
            offsetInGroup,
        } as SortableOffset;

        this._sortableOffsets.set(id, o);

        if (!this._isPaused) {
            // Regenerate 2D array
            this._generate2DArray();
        } else {
            this._needsGenerating = true;
        }
    }

    delete(id: string | number) {
        this._sortableOffsets.delete(id);
    }

    deleteAll() {
        this._sortableOffsets = new Map();
    }

    private _generate2DArray() {
        this._sortedGroupedPrimitives = generate(this._sortableOffsets);
    }

    public updateByGroupIndex(groupIndex: number) {
        const primitivesToUpdate = _getPrimitivesToUpdate(
            this._sortedGroupedPrimitives,
            groupIndex,
        );
        _manipulateOffsets(this._maxRange, primitivesToUpdate);
    }

    public updateAll() {
        // Process them
        _manipulateOffsets(this._maxRange, this._sortedGroupedPrimitives);
    }
}

function _getPrimitivesToUpdate(sortedGroupedPrimitives: HasEyeOffset[][], groupIndex: number) {
    // loop through the nested array picking out the primitives that we want to update
    const primitivesToUpdate = [];
    sortedGroupedPrimitives.forEach((primitiveArray) => {
        const filteredPrimitives = primitiveArray.filter(
            (primitive) => primitive.groupIndex === groupIndex,
        );
        if (filteredPrimitives.length) {
            primitivesToUpdate.push(
                primitiveArray.filter((primitive) => primitive.groupIndex === groupIndex),
            );
        }
    });
    return primitivesToUpdate;
}

function _manipulateOffsets(maxRange: number, orderedOffsets: HasEyeOffset[][]) {
    const totalGroups = orderedOffsets.length;
    let i = totalGroups;
    while (i !== 0) {
        --i;
        const offsetsInGroup = orderedOffsets[i];
        const totalOffsetsInGroup = offsetsInGroup.length;
        let j = totalOffsetsInGroup;
        while (j !== 0) {
            --j;
            const primitive = offsetsInGroup[j];
            _manipulateOffset(primitive, maxRange, i, totalGroups, j, totalOffsetsInGroup);
        }
    }
}

function _manipulateOffset(
    // offset: Cesium.Cartesian3,
    primitive: HasEyeOffset,
    maxRange: number,
    currentGroup: number,
    totalGroups: number,
    currentOffset: number,
    totalOffsetsInGroup: number,
) {
    // Find our min/max given range split by groups
    const max = (maxRange / totalGroups) * (currentGroup + 1);
    const min = (maxRange / totalGroups) * currentGroup;
    // Similarly, find our value within this range and place it in the middle of it's min/max.
    const between = ((max - min) / totalOffsetsInGroup) * (currentOffset + 0.5);
    const z = min + between;
    // The offset must be a negative value as we're moving it towards the camera.
    primitive.eyeOffset = new Cesium.Cartesian3(0, 0, -z);
}
